"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { Drop as DropType, ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { useMemo } from "react";
import DropContext from "./DropContext";
import ParticipationDrop from "./participation/ParticipationDrop";
import WaveDrop from "./WaveDrop";
import WinnerDrop from "./winner/WinnerDrop";
export interface DropInteractionParams {
  drop: ExtendedDrop;
  partId: number;
}
export enum DropLocation {
  MY_STREAM = "MY_STREAM",
  WAVE = "WAVE",
  PROFILE = "PROFILE",
}
interface DropProps {
  readonly drop: ExtendedDrop;
  readonly previousDrop: DropType | null;
  readonly nextDrop: DropType | null;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly dropViewDropId: string | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly parentContainerRef?: React.RefObject<HTMLElement | null> | undefined;
  readonly wrapContentOnly?: (content: React.ReactNode) => React.ReactNode;
}
export default function Drop(t0) {
  const $ = _c(48);
  const {
    drop,
    previousDrop,
    nextDrop,
    showWaveInfo,
    activeDrop,
    location,
    dropViewDropId,
    onReply,
    onReplyClick,
    onQuoteClick,
    onDropContentClick,
    showReplyAndQuote,
    parentContainerRef,
    wrapContentOnly
  } = t0;
  let t1;
  if ($[0] !== activeDrop || $[1] !== drop || $[2] !== location || $[3] !== onDropContentClick || $[4] !== onQuoteClick || $[5] !== onReply || $[6] !== parentContainerRef || $[7] !== showReplyAndQuote || $[8] !== showWaveInfo) {
    t1 = <ParticipationDrop drop={drop} showWaveInfo={showWaveInfo} activeDrop={activeDrop} location={location} onReply={onReply} onQuoteClick={onQuoteClick} onDropContentClick={onDropContentClick} showReplyAndQuote={showReplyAndQuote} parentContainerRef={parentContainerRef} />;
    $[0] = activeDrop;
    $[1] = drop;
    $[2] = location;
    $[3] = onDropContentClick;
    $[4] = onQuoteClick;
    $[5] = onReply;
    $[6] = parentContainerRef;
    $[7] = showReplyAndQuote;
    $[8] = showWaveInfo;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== activeDrop || $[11] !== drop || $[12] !== dropViewDropId || $[13] !== location || $[14] !== nextDrop || $[15] !== onDropContentClick || $[16] !== onQuoteClick || $[17] !== onReply || $[18] !== onReplyClick || $[19] !== parentContainerRef || $[20] !== previousDrop || $[21] !== showReplyAndQuote || $[22] !== showWaveInfo) {
    t2 = <WinnerDrop drop={drop} previousDrop={previousDrop} nextDrop={nextDrop} showWaveInfo={showWaveInfo} activeDrop={activeDrop} location={location} dropViewDropId={dropViewDropId} onReply={onReply} onReplyClick={onReplyClick} onQuoteClick={onQuoteClick} onDropContentClick={onDropContentClick} showReplyAndQuote={showReplyAndQuote} parentContainerRef={parentContainerRef} />;
    $[10] = activeDrop;
    $[11] = drop;
    $[12] = dropViewDropId;
    $[13] = location;
    $[14] = nextDrop;
    $[15] = onDropContentClick;
    $[16] = onQuoteClick;
    $[17] = onReply;
    $[18] = onReplyClick;
    $[19] = parentContainerRef;
    $[20] = previousDrop;
    $[21] = showReplyAndQuote;
    $[22] = showWaveInfo;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  const t3 = previousDrop?.type === DropSize.FULL ? previousDrop : null;
  const t4 = nextDrop?.type === DropSize.FULL ? nextDrop : null;
  let t5;
  if ($[24] !== activeDrop || $[25] !== drop || $[26] !== dropViewDropId || $[27] !== location || $[28] !== onDropContentClick || $[29] !== onQuoteClick || $[30] !== onReply || $[31] !== onReplyClick || $[32] !== showReplyAndQuote || $[33] !== showWaveInfo || $[34] !== t3 || $[35] !== t4 || $[36] !== wrapContentOnly) {
    t5 = <WaveDrop drop={drop} previousDrop={t3} nextDrop={t4} showWaveInfo={showWaveInfo} activeDrop={activeDrop} location={location} dropViewDropId={dropViewDropId} onReply={onReply} onReplyClick={onReplyClick} onQuoteClick={onQuoteClick} onDropContentClick={onDropContentClick} showReplyAndQuote={showReplyAndQuote} wrapContentOnly={wrapContentOnly} />;
    $[24] = activeDrop;
    $[25] = drop;
    $[26] = dropViewDropId;
    $[27] = location;
    $[28] = onDropContentClick;
    $[29] = onQuoteClick;
    $[30] = onReply;
    $[31] = onReplyClick;
    $[32] = showReplyAndQuote;
    $[33] = showWaveInfo;
    $[34] = t3;
    $[35] = t4;
    $[36] = wrapContentOnly;
    $[37] = t5;
  } else {
    t5 = $[37];
  }
  let t6;
  if ($[38] !== t1 || $[39] !== t2 || $[40] !== t5) {
    t6 = {
      [ApiDropType.Participatory]: t1,
      [ApiDropType.Winner]: t2,
      [ApiDropType.Chat]: t5
    };
    $[38] = t1;
    $[39] = t2;
    $[40] = t5;
    $[41] = t6;
  } else {
    t6 = $[41];
  }
  const components = t6;
  let t7;
  if ($[42] !== drop || $[43] !== location) {
    t7 = {
      drop,
      location
    };
    $[42] = drop;
    $[43] = location;
    $[44] = t7;
  } else {
    t7 = $[44];
  }
  const memoizedValue = t7;
  const t8 = components[drop.drop_type];
  let t9;
  if ($[45] !== memoizedValue || $[46] !== t8) {
    t9 = <DropContext.Provider value={memoizedValue}>{t8}</DropContext.Provider>;
    $[45] = memoizedValue;
    $[46] = t8;
    $[47] = t9;
  } else {
    t9 = $[47];
  }
  return t9;
}